import { useDispatch, useSelector } from 'react-redux';
import { OrgUrls, fetchOrgData } from '@api/org';
import { populate } from '@features/redux/slices/orgs';
import { useCallback, useEffect } from 'react';
import { useSetLoading } from './useSetLoading';
import { useAlert } from './useAlert';

import type { RootState } from '@features/redux/store';
import type { NameMap, OrgMap, Countries, LocationMap } from '@api/org';
import type { OrgDispatchValue } from '@features/redux/slices/orgs';
import type { Awaitable, Entries } from '@/types/general';

const fetchAllOrgData = async (token: string, populated: boolean): Promise<OrgDispatchValue> => {
    const names = fetchOrgData<NameMap>(token, OrgUrls.NameMap);
    const map = fetchOrgData<OrgMap>(token, OrgUrls.OrgMap);
    const countries = !populated ? fetchOrgData<Countries>(token, OrgUrls.Countries) : undefined;
    const locations = fetchOrgData<{ locations: Entries<LocationMap> }>(token, OrgUrls.LocationMap);

    const [nameMap, orgMap, { locations: locationEntries }, countryList] = await Promise.all([
        names,
        map,
        locations,
        countries,
    ]);

    return {
        ...nameMap,
        ...orgMap,
        countries: countryList,
        locationMap: locationEntries,
    };
};

export const useOrgs = () => {
    const { IdToken } = useSelector(({ auth }: RootState) => auth);

    const { populated, ...rest } = useSelector(({ orgs }: RootState) => orgs);
    const { setLoading } = useSetLoading();
    const dispatch = useDispatch();
    const { setAlert } = useAlert();

    const refresh = useCallback(async () => {
        if (!IdToken) return;

        setLoading(true);
        try {
            const data = await fetchAllOrgData(IdToken, populated);
            dispatch(populate(data));
        } catch (error) {
            setAlert('An error occurred while fetching organization data.', 'error');
        } finally {
            setLoading(false);
        }
    }, [IdToken, populated]);

    // If not yet populated, run the initial population
    useEffect(() => {
        if (populated || !IdToken) return;
        refresh();
    }, [refresh]);

    const onReady = (handler: () => Awaitable<void>) => {
        return useEffect(() => {
            if (!populated) return;
            handler();
        }, [populated]);
    };

    return {
        /**
         * Whether or not the org data has been populated.
         */
        populated,
        ...rest,
        /**
         * Re-fetch and refresh the store's data.
         */
        refresh,
        /**
         * A hook that will ensure the org data is populated before
         * running a certain function.
         */
        onReady,
    };
};
